// Admin → Client proof. Backs GET /api/admin/client-proof/summary
// (backend/app/routers/admin_client_proof.py). This page is the go/no-go
// evidence for flipping CLIENT_PROOF_MODE from log to enforce.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage, { AdminWindowSwitcher } from '../components/AdminShell';

const WINDOWS = [{ value: 1, label: '24 hours' }, { value: 7, label: '7 days' }, { value: 30, label: '30 days' }];
const VERDICTS = ['pass', 'fail', 'client_error', 'unavailable', 'missing', 'deferred'];

// API timestamps are UTC without a Z.
const formatDateTime = (iso) => (iso ? new Date(`${iso}Z`).toLocaleString() : '');

function byPlatform(breakdown) {
  const out = {};
  breakdown.forEach(({ platform, verdict, count }) => {
    out[platform] = out[platform] || { total: 0 };
    out[platform][verdict] = (out[platform][verdict] || 0) + count;
    out[platform].total += count;
  });
  return out;
}

export default function AdminClientProofPage() {
  const { API_URL } = useAuth();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError('');
    axios.get(`${API_URL}/admin/client-proof/summary`, {
      params: { days },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setError('Could not load client-proof data.'); });
    return () => { cancelled = true; };
  }, [API_URL, days]);

  const platforms = data ? byPlatform(data.breakdown) : {};

  return (
    <AdminPage
      active="reports"
      title="Client proof"
      subtitle="Sign-ins checked for a genuine app or a human browser"
      actions={<AdminWindowSwitcher options={WINDOWS} value={days} onChange={setDays} />}
    >
      {error && <div className="adm-error" role="alert">{error}</div>}
      {data && (
        <>
          <p className="adm-cell__meta">Mode: {data.mode}</p>
          <div className="adm__card adm__card--flush">
            <div className="adm-tablewrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th className="adm-th--right">Pass rate</th>
                    {VERDICTS.map((v) => <th key={v} className="adm-th--right">{v}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(platforms).map(([platform, row]) => {
                    const decided = (row.pass || 0) + (row.fail || 0) + (row.client_error || 0);
                    return (
                      <tr key={platform}>
                        <td>{platform}</td>
                        <td className="adm-td--num">{decided ? `${Math.round(((row.pass || 0) / decided) * 100)}%` : '—'}</td>
                        {VERDICTS.map((v) => <td key={v} className="adm-td--num">{row[v] || 0}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="adm__card adm__card--flush" style={{ marginTop: 16 }}>
            <div className="adm-card__head"><h2 className="adm-card__title">Accounts that failed</h2></div>
            {data.failing_accounts.length === 0 ? (
              <div className="adm-empty">None in this window.</div>
            ) : (
              <div className="adm-tablewrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Account</th><th>Platform</th><th>Reason</th>
                      <th className="adm-th--right">Count</th><th>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.failing_accounts.map((a) => (
                      <tr key={`${a.user_id}-${a.platform}-${a.reason}`}>
                        <td>{a.email}</td><td>{a.platform}</td><td>{a.reason}</td>
                        <td className="adm-td--num">{a.count}</td><td>{formatDateTime(a.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </AdminPage>
  );
}
